/**
 * Performance **Review** — team reviews + submit a manager review, `/hr/performance`,
 * WP-W8. `web/shapes/hr-performance.md` §7 is the agreed shape.
 *
 * Rendered only for a session holding `hr.view_reviews` (`PerformanceScreen` decides
 * that, the same client-side honesty gate `LeaveScreen`'s approvals section uses —
 * never the authorisation, which the server still runs on every call).
 *
 * A typed employee id drives both halves: the employee's reviews (self- and
 * manager/peer-authored alike) and the subject of a manager review this reviewer
 * files. `GET /employees` (Task T2-A) and `EmployeePicker` (Task T2-B) exist now, but
 * this panel has not been rewired to use them, so the id is still typed. The submit
 * form has **no reviewer field of any kind** — `reviewer_employee_id` is never
 * client-supplied (`api/hr.ts`'s own header on `ReviewSubmitRequest`) — and `kind` is
 * fixed to `'manager'`, not a control a person sets. `cycle_id` is also a typed field,
 * for an unrelated reason: nothing on this screen reads
 * `GET /hr/performance/review-cycles` into a shared cache this form could draw from.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { Review, ReviewSubmitRequest } from '../../api/hr';
import { REVIEW_KIND_LABEL } from '../../hr/performance';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { SEVERITY_ICON } from '../../states/icons';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

export interface TeamReviewsPanelProps {
  readonly employeeId: string | null;
  readonly onLoadEmployee: (id: string) => void;
  readonly cycleFilter: string;
  readonly onCycleFilterChange: (value: string) => void;

  readonly listStatus: QueryStatus;
  readonly reviews: readonly Review[] | null;
  readonly listError: WorkforceApiError | null;
  readonly onReload: () => void;

  readonly submitPending: boolean;
  readonly submitError: WorkforceApiError | null;
  readonly submitted: Review | null;
  /** `kind`, and `employee_id`, are decided by this panel from `employeeId` above. */
  readonly onSubmitManagerReview: (body: ReviewSubmitRequest) => void;
}

export function TeamReviewsPanel(props: TeamReviewsPanelProps): ReactNode {
  const { t } = useI18n();
  const [employeeIdInput, setEmployeeIdInput] = useState('');
  const [cycleId, setCycleId] = useState('');
  const [overall, setOverall] = useState('');
  const [narrative, setNarrative] = useState('');
  const [scoreId, setScoreId] = useState('');

  const submitReady = props.employeeId !== null && cycleId.trim() !== '';

  return (
    <section className="wp-section" aria-labelledby="w8-review-heading" data-testid="w8-review">
      <h2 className="wp-section__title" id="w8-review-heading">
        {t('hr.performance.review.title')}
      </h2>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = employeeIdInput.trim();
          if (trimmed === '') return;
          props.onLoadEmployee(trimmed);
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w8-team-employee-id">
            {t('hr.employee.idLabel')}
          </label>
          <input
            className="wp-field__input"
            id="w8-team-employee-id"
            data-testid="w8-team-employee-id"
            type="text"
            inputMode="numeric"
            value={employeeIdInput}
            autoComplete="off"
            onChange={(event) => {
              setEmployeeIdInput(event.target.value);
            }}
          />
          <p className="wp-small wp-muted">{t('hr.employee.idHint')}</p>
        </div>
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w8-team-cycle-filter">
            {t('hr.performance.teamReviews.cycleFilter')}
          </label>
          <input
            className="wp-field__input"
            id="w8-team-cycle-filter"
            data-testid="w8-team-cycle-filter"
            type="text"
            inputMode="numeric"
            value={props.cycleFilter}
            autoComplete="off"
            onChange={(event) => {
              props.onCycleFilterChange(event.target.value);
            }}
          />
          <p className="wp-small wp-muted">{t('hr.performance.teamReviews.cycleFilterHint')}</p>
        </div>
        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w8-team-load"
            disabled={employeeIdInput.trim() === ''}
          >
            {t('hr.performance.teamReviews.load')}
          </button>
        </div>
      </form>

      {props.employeeId === null ? (
        <EmptyState
          title={t('hr.performance.teamReviews.none.title')}
          description={t('hr.performance.teamReviews.none.body')}
          testId="w8-team-none"
        />
      ) : (
        <>
          {props.listStatus === 'loading' ? (
            <Skeleton
              rows={2}
              variant="block"
              label={t('hr.performance.teamReviews.subject')}
              testId="w8-team-skeleton"
            />
          ) : null}
          {props.listStatus === 'error' && props.listError !== null ? (
            <ApiErrorNote
              error={props.listError}
              onRetry={props.onReload}
              subject={t('hr.performance.teamReviews.subject')}
              testId="w8-team-error"
            />
          ) : null}
          {props.reviews === null ? null : props.reviews.length === 0 ? (
            <EmptyState
              title={t('hr.performance.teamReviews.empty.title')}
              description={t('hr.performance.teamReviews.empty.body')}
              testId="w8-team-empty"
            />
          ) : (
            <ul className="wp-rows" data-testid="w8-team-list">
              {props.reviews.map((review) => (
                <li className="wp-row" key={review.id} data-testid={`w8-team-review-${review.id}`}>
                  <div className="wp-row__main">
                    <p>
                      <Badge
                        severity="info"
                        icon={SEVERITY_ICON.info}
                        label={t(REVIEW_KIND_LABEL[review.kind])}
                        testId={`w8-team-review-kind-${review.id}`}
                      />
                    </p>
                    <p className="wp-small wp-muted">
                      {review.overall === null
                        ? t('hr.performance.myReviews.row.noOverall')
                        : t('hr.performance.myReviews.row.overall', { overall: review.overall })}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* ── Submit a manager review ─────────────────────────────────────── */}
          <div className="wp-section__subsection" data-testid="w8-manager-review">
            <h3 className="wp-section__title">{t('hr.performance.submitReview.title')}</h3>
            <p className="wp-small wp-muted">{t('hr.performance.submitReview.intro')}</p>
            <form
              className="wp-filters"
              onSubmit={(event) => {
                event.preventDefault();
                if (props.employeeId === null || !submitReady || props.submitPending) return;
                const trimmedOverall = overall.trim();
                const trimmedNarrative = narrative.trim();
                const trimmedScore = scoreId.trim();
                props.onSubmitManagerReview({
                  cycle_id: cycleId.trim(),
                  kind: 'manager',
                  employee_id: props.employeeId,
                  ...(trimmedOverall === '' ? {} : { overall: Number(trimmedOverall) }),
                  ...(trimmedNarrative === '' ? {} : { narrative: trimmedNarrative }),
                  ...(trimmedScore === '' ? {} : { productivity_score_id: trimmedScore }),
                });
              }}
            >
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w8-manager-review-cycle">
                  {t('hr.performance.cycleIdLabel')}
                </label>
                <input
                  className="wp-field__input"
                  id="w8-manager-review-cycle"
                  data-testid="w8-manager-review-cycle"
                  type="text"
                  inputMode="numeric"
                  value={cycleId}
                  autoComplete="off"
                  onChange={(event) => {
                    setCycleId(event.target.value);
                  }}
                />
              </div>
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w8-manager-review-overall">
                  {t('hr.performance.selfReview.overall')}
                </label>
                <input
                  className="wp-field__input"
                  id="w8-manager-review-overall"
                  data-testid="w8-manager-review-overall"
                  type="number"
                  min="1"
                  max="5"
                  value={overall}
                  onChange={(event) => {
                    setOverall(event.target.value);
                  }}
                />
              </div>
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w8-manager-review-narrative">
                  {t('hr.performance.selfReview.narrative')}
                </label>
                <textarea
                  className="wp-field__input"
                  id="w8-manager-review-narrative"
                  data-testid="w8-manager-review-narrative"
                  maxLength={4000}
                  value={narrative}
                  onChange={(event) => {
                    setNarrative(event.target.value);
                  }}
                />
              </div>
              <div className="wp-filters__group">
                <label className="wp-field__label" htmlFor="w8-manager-review-score">
                  {t('hr.performance.selfReview.productivityScoreId')}
                </label>
                <input
                  className="wp-field__input"
                  id="w8-manager-review-score"
                  data-testid="w8-manager-review-score"
                  type="text"
                  inputMode="numeric"
                  value={scoreId}
                  autoComplete="off"
                  onChange={(event) => {
                    setScoreId(event.target.value);
                  }}
                />
              </div>
              <div className="wp-filters__actions">
                <button
                  className="wp-button wp-button--primary"
                  type="submit"
                  data-testid="w8-manager-review-submit"
                  disabled={!submitReady || props.submitPending}
                >
                  {props.submitPending
                    ? t('hr.performance.submitReview.submitting')
                    : t('hr.performance.submitReview.submit')}
                </button>
              </div>
            </form>
            {props.submitError === null ? null : (
              <ApiErrorNote
                error={props.submitError}
                subject={t('hr.performance.submitReview.subject')}
                testId="w8-manager-review-error"
              />
            )}
            {props.submitted === null ? null : (
              <StatusNote
                severity="low"
                title={t('hr.performance.submitReview.ok')}
                testId="w8-manager-review-ok"
              />
            )}
          </div>
        </>
      )}
    </section>
  );
}
